/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  TextInput,
  Platform,
  Pressable,
  Animated,
  Modal,
  Alert,
  Dimensions,
  ScrollView,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { setShiftHandoverImage } from "../../store/slices/shiftHandOverSlice";
import { createApiClient } from "../../api/apiClient";
import { launchImageLibrary, launchCamera } from "react-native-image-picker";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import LHProgressBar from "../../components/LHProgressBar";
import { useToast } from "../../components/LhToastContainer";
import { useTheme } from "../../theme";

// Helper to check for web/document safely
const isWeb =
  typeof (globalThis as any).document !== "undefined" && Platform.OS === "web";

// Add a constant for EMPTY_TEXT to match LeadershipWalkRedirectActivityImageUpload
const EMPTY_TEXT = " ";

interface ImageData {
  imageUri: string | null;
  description: string;
}

interface ShiftHandoverActivityImageUploadProps {
  visible: boolean;
  onClose: () => void;
  questionId: string;
  questionText: string;
}

const ShiftHandoverActivityImageUpload: React.FC<
  ShiftHandoverActivityImageUploadProps
> = ({ visible, onClose, questionId, questionText }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  const dispatch = useDispatch();
  const toast = useToast();
  const shiftHandoverImages = useSelector(
    (state: RootState) => state.shiftHandOver.shiftHandoverImages
  );
  const handoverState = useSelector(
    (state: RootState) => state.shiftHandOver.handoverState
  );

  // Component state
  const [images, setImages] = useState<ImageData[]>([
    { imageUri: null, description: questionText },
    { imageUri: null, description: questionText },
  ]);

  // Reset state and load images when questionId changes or component becomes visible
  React.useEffect(() => {
    if (visible) {
      // Find existing images for this question
      const existingImages = shiftHandoverImages.filter(
        (img) => img.questionId === questionId
      );

      // Find image with imageId="1" and imageId="2" specifically
      const firstImage = existingImages.find((img) => img.imageId === "1");
      const secondImage = existingImages.find((img) => img.imageId === "2");

      // If slot 1 has an image, but slot 2 does not, clear slot 2's description
      // If slot 2 has an image, but slot 1 does not, clear slot 1's description
      const slot1HasImage = !!(
        firstImage?.imagePreview || firstImage?.imageBase64
      );
      const slot2HasImage = !!(
        secondImage?.imagePreview || secondImage?.imageBase64
      );

      // Reset the state with images for this specific question
      setImages([
        {
          imageUri: firstImage?.imagePreview || firstImage?.imageBase64 || null,
          description:
            !slot1HasImage && slot2HasImage
              ? EMPTY_TEXT
              : firstImage?.description !== undefined
              ? firstImage.description
              : questionText,
        },
        {
          imageUri:
            secondImage?.imagePreview || secondImage?.imageBase64 || null,
          description:
            !slot2HasImage && slot1HasImage
              ? EMPTY_TEXT
              : secondImage?.description !== undefined
              ? secondImage.description
              : questionText,
        },
      ]);

      // Reset other state values
      setErrorMessages([null, null]);
      setUploadProgress([0, 0]);
      setUploadingStates([false, false]);
    }
  }, [questionId, visible, shiftHandoverImages, questionText]);

  const [errorMessages, setErrorMessages] = useState<(string | null)[]>([
    null,
    null,
  ]);
  const [uploadProgress, setUploadProgress] = useState<number[]>([0, 0]);
  const [uploadingStates, setUploadingStates] = useState<boolean[]>([
    false,
    false,
  ]);

  // Create animated values for each image
  const iconScales = useRef([
    new Animated.Value(1),
    new Animated.Value(1),
  ]).current;
  const circleScales = useRef([
    new Animated.Value(1),
    new Animated.Value(1),
  ]).current;
  const borderColors = useRef([
    new Animated.Value(0),
    new Animated.Value(0),
  ]).current;

  // Function to handle hover state for web with smooth animations
  const handleHoverIn = (index: number) => {
    if (isWeb) {
      Animated.parallel([
        Animated.timing(iconScales[index], {
          toValue: 1.2,
          duration: 250,
          useNativeDriver: true,
        }),
        Animated.timing(circleScales[index], {
          toValue: 1.1,
          duration: 250,
          useNativeDriver: true,
        }),
        Animated.timing(borderColors[index], {
          toValue: 1,
          duration: 250,
          useNativeDriver: false,
        }),
      ]).start();
    }
  };

  // Function to handle hover out state for web
  const handleHoverOut = (index: number) => {
    if (isWeb) {
      Animated.parallel([
        Animated.timing(iconScales[index], {
          toValue: 1,
          duration: 250,
          useNativeDriver: true,
        }),
        Animated.timing(circleScales[index], {
          toValue: 1,
          duration: 250,
          useNativeDriver: true,
        }),
        Animated.timing(borderColors[index], {
          toValue: 0,
          duration: 250,
          useNativeDriver: false,
        }),
      ]).start();
    }
  };

  // Helper function to handle mobile image selection (camera or gallery)
  const handleMobileImageSelection = async (
    index: number,
    source: "camera" | "gallery"
  ) => {
    const options = {
      mediaType: "photo" as const,
      quality: 1 as const,
    };

    let result;
    try {
      if (source === "camera") {
        result = await launchCamera(options);
      } else {
        result = await launchImageLibrary(options);
      }
    } catch (error: any) {
      console.error("Error launching camera/gallery:", error);
      setErrorMessages((prev) => {
        const newErrors = [...prev];
        newErrors[index] = "Error accessing camera/gallery";
        return newErrors;
      });
      return;
    }

    if (result.didCancel) {
      console.log("User cancelled image selection");
      return;
    }

    if (result.errorMessage) {
      console.error("Image picker error:", result.errorMessage);
      setErrorMessages((prev) => {
        const newErrors = [...prev];
        newErrors[index] = result.errorMessage || "Error selecting image";
        return newErrors;
      });
      return;
    }

    if (result.assets && result.assets.length > 0) {
      const asset = result.assets[0];

      if (asset.fileSize && asset.fileSize > 5 * 1024 * 1024) {
        setErrorMessages((prev) => {
          const newErrors = [...prev];
          newErrors[index] = "File size exceeds 10MB limit";
          return newErrors;
        });
        return;
      }

      // Only set uploading states after image is selected and validated
      setUploadingStates((prev) => {
        const newStates = [...prev];
        newStates[index] = true;
        return newStates;
      });

      // Ensure progress is initialized to 0
      setUploadProgress((prev) => {
        const arr = [...prev];
        arr[index] = 0;
        return arr;
      });

      const formData = new FormData();
      formData.append("image", {
        uri: asset.uri,
        type: asset.type,
        name: asset.fileName || `image_${Date.now()}.jpg`,
      } as any);
      formData.append("containerName", "shifthandover");

      try {
        const api = createApiClient();
        const response = await api.post("/common/images/upload", formData, {
          headers: {
            Accept: "application/json",
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent: any) => {
            if (progressEvent && progressEvent.total) {
              const progress = Math.min(
                0.5,
                progressEvent.loaded / progressEvent.total
              );
              setUploadProgress((prev) => {
                const arr = [...prev];
                arr[index] = progress;
                return arr;
              });
            }
          },
        });

        if (response && response.data && response.data.success === true) {
          const { imageUrl, fileName } = response.data.data;

          // Update local state immediately to show image
          const updatedImages = [...images];
          updatedImages[index].imageUri = imageUrl;
          setImages(updatedImages);

          // Update Redux state with the new image
          dispatch(
            setShiftHandoverImage({
              imageId: `${index + 1}`,
              imageBase64: fileName,
              imagePreview: imageUrl,
              description:
                images[index].description === ""
                  ? EMPTY_TEXT
                  : images[index].description,
              questionId,
            })
          );

          // Set progress to 100% to show completion
          setUploadProgress((prev) => {
            const arr = [...prev];
            arr[index] = 1;
            return arr;
          });

          toast.showToast({
            header: "Success",
            subtext: "Image uploaded successfully",
            variant: "success",
          });
        } else {
          throw new Error(response?.data?.message || "Upload failed");
        }
      } catch (error: any) {
        console.error("Upload error:", error);
        setErrorMessages((prev) => {
          const newErrors = [...prev];
          newErrors[index] = error.message || "Upload failed";
          return newErrors;
        });
      } finally {
        setUploadingStates((prev) => {
          const newStates = [...prev];
          newStates[index] = false;
          return newStates;
        });
      }
    } else {
      setErrorMessages((prev) => {
        const newErrors = [...prev];
        newErrors[index] = "No image selected";
        return newErrors;
      });
    }
  };

  const handleImageUpload = async (index: number) => {
    // Prevent upload if in completed state or already uploading
    if (uploadingStates[index] || handoverState === "completed") {
      return;
    }

    // Reset error for this image
    setErrorMessages((prev) => {
      const newErrors = [...prev];
      newErrors[index] = null;
      return newErrors;
    });

    if (isWeb) {
      const input = (globalThis as any).document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = async (event: any) => {
        const file = event.target.files[0];
        if (file) {
          if (file.size > 10 * 1024 * 1024) {
            setErrorMessages((prev) => {
              const newErrors = [...prev];
              newErrors[index] = "File size exceeds 10MB limit";
              return newErrors;
            });
            return;
          }

          // Only set uploading states after file is selected and validated
          setUploadingStates((prev) => {
            const newStates = [...prev];
            newStates[index] = true;
            return newStates;
          });

          // Ensure progress is initialized to 0
          setUploadProgress((prev) => {
            const arr = [...prev];
            arr[index] = 0;
            return arr;
          });

          // Don't set upload progress until API call starts
          const formData = new FormData();
          formData.append("image", file);
          formData.append("containerName", "shifthandover"); // Add containerName for ShiftHandOver

          try {
            const api = createApiClient();
            const response = await api.post("/common/images/upload", formData, {
              headers: {
                Accept: "application/json",
                // Do NOT set Content-Type, let browser/axios set it for FormData
              },
              onUploadProgress: (progressEvent: any) => {
                if (progressEvent && progressEvent.total) {
                  // Cap progress at 50% until API call is complete
                  const progress = Math.min(
                    0.5,
                    progressEvent.loaded / progressEvent.total
                  );
                  setUploadProgress((prev) => {
                    const arr = [...prev];
                    // First progress event - set to at least 0.05 to show it started
                    if (prev[index] === 0) {
                      arr[index] = Math.max(0.05, progress);
                    } else {
                      arr[index] = progress;
                    }
                    return arr;
                  });
                }
              },
            });

            if (response && response.data && response.data.success === true) {
              const { imageUrl, fileName } = response.data.data;

              // Update local state immediately to show image
              const updatedImages = [...images];
              updatedImages[index].imageUri = imageUrl;
              setImages(updatedImages);

              // Update Redux state with the new image
              dispatch(
                setShiftHandoverImage({
                  imageId: `${index + 1}`,
                  imageBase64: fileName, // Store the file name instead of the URL
                  imagePreview: imageUrl, // Store the image URL for preview
                  description:
                    images[index].description === ""
                      ? EMPTY_TEXT
                      : images[index].description,
                  questionId,
                })
              );

              // Show success toast notification
              toast.showToast({
                header: "Success",
                subtext: "Image uploaded successfully",
                variant: "success",
              });

              // Set progress to 100% only after successful response
              setUploadProgress((prev) => {
                const arr = [...prev];
                arr[index] = 1;
                return arr;
              });
              // Show 100% briefly before hiding progress bar
              await new Promise((resolve) => setTimeout(resolve, 500));
            } else {
              // Get error message from response or use default
              const errorMessage =
                response?.data?.message || "Failed to upload image";
              const errorDetail = response?.data?.error || "Unknown error";

              // Set error message in state
              setErrorMessages((prev) => {
                const newErrors = [...prev];
                newErrors[index] = "Unable to upload image";
                return newErrors;
              });

              // Show error toast notification
              toast.showToast({
                header: "Error Uploading Image",
                subtext: `${errorMessage}: ${errorDetail}`,
                variant: "error",
              });
            }
          } catch (e: any) {
            console.error("Image upload error:", e);

            // Set error message in state
            setErrorMessages((prev) => {
              const newErrors = [...prev];
              newErrors[index] = "Unable to upload image";
              return newErrors;
            });

            // Show error toast notification
            toast.showToast({
              header: "Error Uploading Image",
              subtext: e?.message || "An unexpected error occurred",
              variant: "error",
            });
          } finally {
            // Only reset progress and uploading state if there was no error
            if (!errorMessages[index]) {
              setUploadProgress((prev) => {
                const arr = [...prev];
                arr[index] = 0;
                return arr;
              });

              setUploadingStates((prev) => {
                const newStates = [...prev];
                newStates[index] = false;
                return newStates;
              });
            } else {
              // If there was an error, we still need to allow retry
              setUploadingStates((prev) => {
                const newStates = [...prev];
                newStates[index] = false;
                return newStates;
              });
            }
          }
        }
        // If no file selected, do nothing - button remains enabled
      };
      input.click();
    } else {
      // Show action sheet for mobile devices
      Alert.alert(
        "Select Photo",
        "Choose an option to upload a photo",
        [
          {
            text: "Take Photo",
            onPress: () => handleMobileImageSelection(index, "camera"),
          },
          {
            text: "Choose from Gallery",
            onPress: () => handleMobileImageSelection(index, "gallery"),
          },
          {
            text: "Cancel",
            style: "cancel",
          },
        ],
        { cancelable: true }
      );
    }
  };

  const handleDescriptionChange = (index: number, text: string) => {
    // Prevent changes if in completed state
    if (handoverState === "completed") {
      return;
    }

    const updatedImages = [...images];
    updatedImages[index].description = text;
    setImages(updatedImages);

    // Update Redux if the image exists
    if (images[index].imageUri) {
      // Find existing image data for this question and index
      const existingImage = shiftHandoverImages.find(
        (img) => img.questionId === questionId && img.imageId === `${index + 1}`
      );

      dispatch(
        setShiftHandoverImage({
          imageId: `${index + 1}`, // 1 or 2 based on which upload area was used
          imageBase64: existingImage?.imageBase64 || "", // Keep the existing file name
          imagePreview: images[index].imageUri, // Keep the existing image preview URL
          description: text === "" ? EMPTY_TEXT : text,
          questionId,
        })
      );
    }
  };

  // Add a constant to determine if the description is truly empty
  const EMPTY_TEXT = " ";

  // Responsive layout detection
  const windowWidth = Platform.OS === "web" ? window.innerWidth : Dimensions.get("window").width;
  const windowHeight = Dimensions.get("window").height;
  
  // Detect iPad: iOS devices with larger screens (typically 768px+ width)
  const isIPad = Platform.OS === "ios" && windowWidth >= 768;
  
  // Tablet includes both web tablets and iPads
  const isTablet = (Platform.OS === "web" && windowWidth >= 768) || isIPad;
  
  // Large screen layout (row layout): web desktops/tablets and iPads
  const isLargeScreen = isTablet || (isWeb && windowWidth >= 768);
  
  const isSmallMobileDevice = () => {
    if (Platform.OS === "ios" || Platform.OS === "android") {
      return (
        Dimensions.get("window").width < 380 ||
        Dimensions.get("window").height < 700
      );
    }
    return false;
  };
  const isCompactMode = isSmallMobileDevice();

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={[styles.modalOverlay, isDarkMode && { backgroundColor: colors.overlay }]}>
        <View
          style={[
            styles.modalContent,
            isCompactMode && styles.modalContentCompact,
            isDarkMode && {
              backgroundColor: colors.background.surface,
              borderColor: colors.border.default,
            },
          ]}
        >
          <View style={styles.modalHeader}>
            <Text style={[styles.modalTitle, isDarkMode && { color: colors.text.primary }]}>
              Upload Images for {questionText}
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <MaterialIcons name="close" size={24} color={isDarkMode ? colors.text.primary : "#333"} />
            </TouchableOpacity>
          </View>

          <ScrollView
            contentContainerStyle={[
              isCompactMode ? styles.containerCompact : styles.container,
              isDarkMode && { backgroundColor: colors.background.surface },
            ]}
            showsVerticalScrollIndicator={true}
          >
            {images.map((image, index) => {
              // Animated border color interpolation
              const borderColor = borderColors[index].interpolate({
                inputRange: [0, 1],
                outputRange: [
                  errorMessages[index] ? "red" : "#ced4da",
                  "#4d94db",
                ],
              });
              const hasImage = !!image.imageUri;
              const isCompleted = handoverState === "completed";

              const getImageContainerHeight = () =>
                isSmallMobileDevice() ? 110 : 150;
              const imageContainerHeight = getImageContainerHeight();

              return (
                <View
                  key={index}
                  style={[
                    styles.observationContainer,
                    isLargeScreen && styles.observationContainerRow,
                    isCompactMode && styles.observationContainerCompact,
                  ]}
                >
                  <View
                    style={[
                      styles.uploadContainer,
                      { height: imageContainerHeight },
                      isCompleted && styles.disabledContainer,
                      isCompactMode && styles.uploadContainerCompact,
                    ]}
                  >
                    {hasImage ? (
                      <Pressable
                        style={[
                          styles.imagePreviewContainer,
                          isCompleted && styles.disabledContainer,
                        ]}
                        onPress={() => !isCompleted && handleImageUpload(index)}
                        disabled={isCompleted}
                      >
                        <View style={styles.imageContainer}>
                          <Image
                            source={
                              image.imageUri
                                ? { uri: image.imageUri }
                                : undefined
                            }
                            style={styles.fullImage}
                            onError={(e) =>
                              console.error(
                                `Image failed to load: ${image.imageUri}`,
                                e.nativeEvent.error
                              )
                            }
                          />
                        </View>
                        {!isCompleted && (
                          <View style={styles.imageOverlay}>
                            <MaterialIcons
                              name="edit"
                              size={24}
                              color="white"
                            />
                            <Text style={styles.overlayText}>
                              Tap to change
                            </Text>
                          </View>
                        )}
                      </Pressable>
                    ) : (
                      <View style={styles.attachmentsContainer}>
                        <View
                          style={[
                            styles.dropZone,
                            isDarkMode && {
                              backgroundColor: colors.background.elevated,
                              borderColor: colors.border.default,
                            },
                            uploadingStates[index] &&
                              styles.dropZoneUploading,
                            errorMessages[index] && styles.dropZoneError,
                          ]}
                        >
                          {uploadingStates[index] &&
                          uploadProgress[index] > 0 ? (
                            <View style={styles.progressContainer}>
                              <LHProgressBar
                                progress={uploadProgress[index]}
                                height={16}
                              />
                              <Text style={styles.progressText}>
                                {uploadProgress[index] >= 0.99
                                  ? "Processing..."
                                  : `${Math.round(
                                      uploadProgress[index] * 100
                                    )}%`}
                              </Text>
                            </View>
                          ) : (
                            <>
                              <View style={styles.iconLabelContainer}>
                                <View style={styles.iconContainer}>
                                  <MaterialIcons
                                    name="camera-alt"
                                    size={24}
                                    color={
                                      errorMessages[index]
                                        ? "red"
                                        : isDarkMode
                                        ? colors.text.primary
                                        : "#3A3A3A"
                                    }
                                  />
                                </View>
                                <Text
                                  style={[
                                    styles.dragDropText,
                                    isDarkMode && { color: colors.text.primary },
                                    errorMessages[index] && styles.errorText,
                                  ]}
                                >
                                  {errorMessages[index] ||
                                    "Drag and Drop your file here"}
                                </Text>
                                <Text style={[styles.dragDropText, isDarkMode && { color: colors.text.secondary }]}>
                                  (only .jpg,.png)
                                </Text>
                              </View>
                              <View style={{ marginTop: 8 }}>
                                <Pressable
                                  style={[
                                    styles.uploadButton,
                                    isDarkMode && {
                                      backgroundColor: colors.background.surface,
                                      borderColor: colors.brand.primary,
                                    },
                                    isCompleted && styles.disabledButton,
                                  ]}
                                  onPress={() =>
                                    !isCompleted && handleImageUpload(index)
                                  }
                                  disabled={
                                    isCompleted || uploadingStates[index]
                                  }
                                  testID={`upload-button-${index}`}
                                >
                                  <View style={styles.buttonContent}>
                                    <View style={styles.uploadIconContainer}>
                                      <MaterialIcons
                                        name="file-upload"
                                        size={14}
                                        color={
                                          isCompleted
                                            ? "#999"
                                            : isDarkMode
                                            ? colors.brand.primary
                                            : "#005CBC"
                                        }
                                      />
                                    </View>
                                    <Text
                                      style={[
                                        styles.uploadButtonText,
                                        isDarkMode && {
                                          color: colors.brand.primary,
                                        },
                                        isCompleted &&
                                          styles.disabledButtonText,
                                      ]}
                                    >
                                      {uploadingStates[index]
                                        ? "Uploading..."
                                        : "Upload"}
                                    </Text>
                                  </View>
                                </Pressable>
                              </View>
                            </>
                          )}
                        </View>
                      </View>
                    )}
                  </View>

                  <View
                    style={[
                      styles.descriptionContainer,
                      isCompactMode && styles.descriptionContainerCompact,
                      isTablet && styles.descriptionContainerTablet,
                    ]}
                  >
                    <Text style={[styles.descriptionHeader, isDarkMode && { color: colors.text.primary }]}>DESCRIPTION</Text>
                    <TextInput
                      style={[
                        styles.descriptionInput,
                        isDarkMode && {
                          backgroundColor: colors.background.elevated,
                          borderColor: colors.input.border,
                          color: colors.text.primary,
                        },
                        handoverState === "completed" && styles.disabledInput,
                        handoverState === "completed" &&
                          isDarkMode && {
                            backgroundColor: colors.background.surface,
                            color: colors.text.disabled,
                            borderColor: colors.border.default,
                          },
                      ]}
                      placeholder="Add Description..."
                      placeholderTextColor={isDarkMode ? colors.text.disabled : "#c0c0c0"}
                      multiline
                      value={
                        image.description === EMPTY_TEXT
                          ? ""
                          : image.description
                      }
                      onChangeText={(text) =>
                        handleDescriptionChange(index, text)
                      }
                      editable={handoverState !== "completed"}
                    />
                  </View>
                </View>
              );
            })}
          </ScrollView>

          <View style={[styles.actionButtonsRow, isCompactMode && styles.actionButtonsRowCompact]}>
            <Pressable
              style={[styles.cancelButton, isCompactMode && styles.cancelButtonCompact]}
              onPress={onClose}
              accessibilityLabel="Cancel"
            >
              <Text
                style={[
                  styles.cancelButtonText,
                  isCompactMode && styles.cancelButtonTextCompact,
                  isDarkMode && { color: colors.brand.primary },
                ]}
              >
                Cancel
              </Text>
            </Pressable>
            <Pressable
              style={[
                styles.proceedButton,
                isCompactMode && styles.proceedButtonCompact,
                isDarkMode && { backgroundColor: colors.brand.primary },
              ]}
              onPress={onClose}
              accessibilityLabel="Proceed"
            >
              <Text style={[styles.proceedButtonText, isCompactMode && styles.proceedButtonTextCompact]}>Proceed</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "white",
    borderRadius: 8,
    borderWidth: 10,
    borderColor: "#D8E7FB",
    padding: 16,
    width: Platform.OS === "web" ? "80%" : "90%",
    maxWidth: 800,
    maxHeight: Platform.OS === "ios" || Platform.OS === "android" ? "80%" : "90%",
  },
  modalContentCompact: {
    padding: 8,
    width: '90%',
  },
  modalTitleCompact: {
    fontSize: 16,
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "bold",
  },
  closeButton: {
    padding: 8,
  },
  container: {
    padding: 16,
    backgroundColor: "#fff",
    borderRadius: 8,
    // borderWidth: 1,
    borderColor: "#e9ecef",
  },
  containerCompact: {
    padding: 8,
  },
  observationContainer: {
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'center',
    marginBottom: 20,
    position: 'relative',
  },
  observationContainerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  observationContainerCompact: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  uploadContainer: {
    width: Platform.OS === "web" || Dimensions.get("window").width >= 768 ? '35%' : '100%',
    height: 150,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    marginBottom: 0,
    overflow: 'visible',
    position: 'relative',
  },
  uploadContainerCompact: {
    width: '100%',
    height: 110,
    marginRight: 0,
    marginBottom: 16,
  },
  imageContainer: {
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  imagePreviewContainer: {
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  fullImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'contain',
  },
  imageOverlay: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: 16,
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
  },
  overlayText: {
    color: 'white',
    fontSize: 12,
    marginLeft: 4,
  },
  attachmentsContainer: {
    width: '100%',
  },
  dropZone: {
    width: '100%',
    paddingHorizontal: 16,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 110,
    backgroundColor: '#F5F9FF',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#D8E7FB',
    borderRadius: 8,
  },
  dropZoneUploading: {
    borderColor: '#0066CC',
    backgroundColor: 'rgba(0,102,204,0.04)',
  },
  dropZoneError: {
    borderColor: 'red',
    backgroundColor: 'rgba(255, 235, 235, 0.8)',
  },
  iconLabelContainer: {
    width: '100%',
    alignItems: 'center',
  },
  iconContainer: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: -8,
  },
  dragDropText: {
    fontWeight: '400',
    fontSize: 12,
    lineHeight: 18,
    textAlign: 'center',
    color: '#3A3A3A',
    width: '100%',
    marginTop: 4,
  },
  uploadButton: {
    height: 24,
    backgroundColor: '#FBFCFE',
    borderWidth: 1,
    borderColor: '#005CBC',
    borderRadius: 24,
    alignSelf: 'stretch',
    paddingHorizontal: 12,
    paddingVertical: 0,
  },
  disabledButton: {
    backgroundColor: '#f2f2f2',
    borderColor: '#999',
  },
  buttonContent: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
    paddingHorizontal: 8,
    paddingVertical: 0,
  },
  uploadIconContainer: {
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadButtonText: {
    fontWeight: '700',
    fontSize: 12,
    lineHeight: 18,
    textAlign: 'center',
    color: '#005CBC',
  },
  disabledButtonText: {
    color: '#999',
  },
  descriptionContainer: {
    flex: Platform.OS === "web" ? 1 : undefined,
    width: Platform.OS === "web" ? undefined : "100%",
    height: 150,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    marginTop: Platform.OS === "web" ? 0 : 16,
  },
  descriptionContainerCompact: {
    height: 110,
  },
  descriptionContainerTablet: {
    flex: 1,
    width: undefined,
    height: 150,
    marginTop: 0,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  descriptionHeader: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#6c757d",
    marginBottom: 8,
  },
  descriptionInput: {
    width: "100%",
    flex: 1,
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 8,
    padding: 8,
    fontSize: 14,
    backgroundColor: "#fff",
    textAlignVertical: "top",
  },
  doneButton: {
    backgroundColor: "#0066CC",
    borderRadius: 8,
    padding: 12,
    marginTop: 16,
    alignItems: "center",
  },
  doneButtonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 16,
  },
  actionButtonsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12,
    alignSelf: 'stretch',
    marginTop: 16,
  },
  actionButtonsRowCompact: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
    alignSelf: 'stretch',
    marginTop: 8,
    paddingHorizontal: 8,
  },
  cancelButton: {
    height: 36,
    paddingHorizontal: 12,
    paddingVertical: 6,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  cancelButtonCompact: {
    paddingVertical: 6,
  },
  cancelButtonText: {
    color: '#005CBC',
    fontWeight: '700',
    fontSize: 14,
  },
  cancelButtonTextCompact: {
    fontSize: 14,
  },
  proceedButton: {
    height: 36,
    backgroundColor: '#005CBC',
    borderRadius: 24,
    paddingHorizontal: 16,
    paddingVertical: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  proceedButtonCompact: {
    paddingVertical: 6,
  },
  proceedButtonText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 14,
  },
  proceedButtonTextCompact: {
    fontSize: 14,
  },
  fullWidth: {
    width: "100%",
  },
  disabledText: {
    color: "#DDD",
  },
  errorContainer: {
    borderColor: "red",
    backgroundColor: "rgba(255, 235, 235, 0.8)",
  },
  pressableContainer: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  progressContainer: {
    width: "96%",
    marginLeft: "2%",
    marginRight: "2%",
    alignItems: "center",
    justifyContent: "center",
  },
  progressText: {
    fontSize: 12,
    marginTop: 8,
  },
  errorBorder: {
    borderColor: "red",
  },
  errorText: {
    color: "red",
  },
  disabledContainer: {
    opacity: 0.6,
    backgroundColor: "#f2f2f2",
    ...(Platform.OS === "web" ? { cursor: "not-allowed" as any } : {}),
  },
  disabledInput: {
    backgroundColor: "#f2f2f2",
    color: "#888888",
  },
});

export default ShiftHandoverActivityImageUpload;
